/**
 * Sales Form Component
 * Comprehensive form for creating service sales with all required fields
 * Including client selection, cost center, salesperson, and items management
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Plus, Trash2, Save, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

import { useServiceSaleForm, useServiceSaleFormDraft } from '@/hooks/useServiceSales';
import { useClients, useCreateClient } from '@/hooks/useServiceSales';
import { useIndustryServicesForSales } from '@/hooks/useIndustryServicesForSales';
import { useCostCentersForSales } from '@/hooks/useCostCentersForSales';
import { useSalesTeam } from '@/hooks/useSalesTeam';
import type { ServiceSaleFormData, ServiceSaleFormItem, ClientFormData } from '@/types/serviceSales';

import { ClientSelector } from './ClientSelector';
import { SalesItemsSection } from './SalesItemsSection';
import { SalesSummary } from './SalesSummary';
import { NewClientDialog } from './NewClientDialog';

// Form schema
const serviceSalesSchema = z.object({
  type: z.enum(['sale', 'budget']),
  client_id: z.string().optional(),
  sale_date: z.string(),
  expiry_date: z.string().optional(),
  cost_center_id: z.string().optional(),
  salesperson_id: z.string().optional(),
  notes: z.string().optional(),
  terms_conditions: z.string().optional(),
  client_notes: z.string().optional(),
  internal_notes: z.string().optional(),
}).refine((data) => {
  // Either client_id must be provided or new_client data must exist
  return true; // We'll handle this in the component
}, {
  message: 'Selecione um cliente ou crie um novo',
});

interface SalesFormProps {
  initialData?: Partial<ServiceSaleFormData>;
  onSave?: (data: ServiceSaleFormData) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export function SalesForm({
  initialData,
  onSave,
  onCancel,
  isLoading = false,
}: SalesFormProps) {
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [formKey] = useState(() => `sales-form-${Date.now()}`);

  // Form hooks
  const { formData, updateForm, addItem, updateItem, removeItem, validate } = useServiceSaleForm(initialData);
  const { saveDraft, loadDraft, clearDraft } = useServiceSaleFormDraft(formKey);

  // React Hook Form
  const form = useForm<z.infer<typeof serviceSalesSchema>>({
    resolver: zodResolver(serviceSalesSchema),
    defaultValues: {
      type: formData.type,
      client_id: formData.client_id || '',
      sale_date: formData.sale_date,
      expiry_date: formData.expiry_date || '',
      cost_center_id: formData.cost_center_id || '',
      salesperson_id: formData.salesperson_id || '',
      notes: formData.notes || '',
      terms_conditions: formData.terms_conditions || '',
      client_notes: formData.client_notes || '',
      internal_notes: formData.internal_notes || '',
    },
  });

  // Data hooks
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const { data: services = [], isLoading: servicesLoading } = useIndustryServicesForSales();
  const { data: costCenters = [], isLoading: costCentersLoading } = useCostCentersForSales();
  const { data: salesTeam = [], isLoading: salesTeamLoading } = useSalesTeam();
  const createClient = useCreateClient();

  // Load draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft && !initialData) {
      updateForm(draft);
      form.reset(draft);
    }
  }, []);

  // Auto-save draft
  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraft(formData);
    }, 2000); // Save after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [formData, saveDraft]);

  // Watch form changes
  const watchedValues = form.watch();
  useEffect(() => {
    updateForm(watchedValues);
  }, [watchedValues, updateForm]);

  const handleSubmit = async (values: z.infer<typeof serviceSalesSchema>) => {
    try {
      // Validate form
      const validation = validate();
      if (!validation.isValid) {
        toast.error('Verifique os erros no formulário');
        // Focus on the first tab with errors
        if (validation.errors.client_id) {
          setActiveTab('basic');
        } else if (validation.errors.items) {
          setActiveTab('items');
        }
        return;
      }

      // Prepare final form data
      const finalData: ServiceSaleFormData = {
        ...formData,
        ...values,
        items: formData.items, // Items are managed separately
      };

      onSave?.(finalData);
      clearDraft();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erro ao salvar formulário');
    }
  };

  const handleClientCreated = (client: any) => {
    // Update form with new client
    form.setValue('client_id', client.id);
    updateForm({ client_id: client.id });
    setShowNewClientDialog(false);
    toast.success('Cliente criado com sucesso!');
  };

  const handleAddItem = () => {
    const newItem: ServiceSaleFormItem = {
      id: uuidv4(),
      description: '',
      quantity: 1,
      unit: 'un',
      unit_price: 0,
      discount_type: 'percentage',
      discount_value: 0,
      tax_percentage: 0,
      total_amount: 0,
    };
    addItem(newItem);
  };

  const handleClearDraft = () => {
    clearDraft();
    form.reset();
    updateForm({
      type: 'sale',
      sale_date: new Date().toISOString().split('T')[0],
      items: [],
      subtotal: 0,
      discount_type: 'percentage',
      discount_value: 0,
      tax_amount: 0,
      total_value: 0,
    });
    toast.success('Rascunho limpo');
  };

  const hasClient = !!formData.client_id;
  const hasItems = formData.items.length > 0;
  const isValid = hasClient && hasItems;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {formData.type === 'budget' ? 'Novo Orçamento' : 'Nova Venda de Serviços'}
          </h2>
          <p className="text-muted-foreground">
            Preencha as informações para criar uma {formData.type === 'budget' ? 'proposta comercial' : 'venda'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleClearDraft} size="sm">
            <X className="h-4 w-4 mr-1" />
            Limpar Rascunho
          </Button>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancelar
            </Button>
          )}
          <Button onClick={form.handleSubmit(handleSubmit)} disabled={!isValid || isLoading}>
            <Save className="h-4 w-4 mr-1" />
            {isLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Informações Básicas</TabsTrigger>
              <TabsTrigger value="items">Itens</TabsTrigger>
              <TabsTrigger value="additional">Adicionais</TabsTrigger>
              <TabsTrigger value="summary">Resumo</TabsTrigger>
            </TabsList>

            {/* Basic Information Tab */}
            <TabsContent value="basic" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Principais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tipo</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="sale">Venda</SelectItem>
                              <SelectItem value="budget">Orçamento</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="sale_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Data da {formData.type === 'budget' ? 'Proposta' : 'Venda'}</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    'w-full pl-3 text-left font-normal',
                                    !field.value && 'text-muted-foreground'
                                  )}
                                >
                                  {field.value ? (
                                    format(new Date(field.value), 'PPP', { locale: ptBR })
                                  ) : (
                                    <span>Selecione uma data</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                onSelect={(date) => field.onChange(date?.toISOString().split('T')[0])}
                                disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="client_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cliente</FormLabel>
                        <div className="flex gap-2">
                          <Select value={field.value || ''} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um cliente" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {clients.map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowNewClientDialog(true)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Novo Cliente
                          </Button>
                        </div>
                        {!hasClient && (
                          <FormDescription>
                            Selecione um cliente existente ou crie um novo para continuar
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="cost_center_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Centro de Custo</FormLabel>
                          <Select value={field.value || ''} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Opcional" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">Nenhum</SelectItem>
                              {costCenters.map((center) => (
                                <SelectItem key={center.id} value={center.id}>
                                  {center.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="salesperson_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Vendedor Responsável</FormLabel>
                          <Select value={field.value || ''} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Opcional" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">Nenhum</SelectItem>
                              {salesTeam.map((person) => (
                                <SelectItem key={person.id} value={person.id}>
                                  {person.full_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Items Tab */}
            <TabsContent value="items" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Itens da {formData.type === 'budget' ? 'Proposta' : 'Venda'}</CardTitle>
                    <Button type="button" onClick={handleAddItem} variant="outline">
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!hasItems ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>Nenhum item adicionado ainda.</p>
                      <p className="text-sm">Clique em "Adicionar Item" para começar.</p>
                    </div>
                  ) : (
                    <SalesItemsSection
                      items={formData.items}
                      services={services}
                      onUpdateItem={updateItem}
                      onRemoveItem={removeItem}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Additional Information Tab */}
            <TabsContent value="additional" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Adicionais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="expiry_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Validade</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(new Date(field.value), 'PPP', { locale: ptBR })
                                ) : (
                                  <span>Selecione uma data</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value ? new Date(field.value) : undefined}
                              onSelect={(date) => field.onChange(date?.toISOString().split('T')[0])}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>
                          Apenas para orçamentos. Data limite para aceitação da proposta.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Observações Internas</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ''}
                            placeholder="Observações internas sobre esta venda..."
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Estas observações não serão visíveis para o cliente
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="terms_conditions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Termos e Condições</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ''}
                            placeholder="Termos e condições específicos desta venda..."
                            rows={4}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="client_notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notas para o Cliente</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ''}
                            placeholder="Informações importantes que o cliente deve ver..."
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Summary Tab */}
            <TabsContent value="summary" className="space-y-6">
              <SalesSummary formData={formData} />
            </TabsContent>
          </Tabs>
        </form>
      </Form>

      {/* New Client Dialog */}
      <NewClientDialog
        open={showNewClientDialog}
        onOpenChange={setShowNewClientDialog}
        onClientCreated={handleClientCreated}
      />
    </div>
  );
}